import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Input } from '@/app/components/ui/Input';
import { Label } from '@/app/components/ui/Label';
import { Button } from '@/app/components/ui/Button';
import { ActionCard } from '@/app/components/ui/ActionCard';
import { AddressSection } from '../AddressSection';
import { AgeSelection } from './AgeSelection';
import { addressSchema } from '@/lib/schema';

// Base schema for a person
const createPersonSchema = (options: {
  showAgeSelection?: boolean;
  requireEmail?: boolean;
  isProfessional?: boolean;
}) => {
  let schema = yup.object({
    fullName: yup.string().required('Full name is required')
      .test('is-full-name', 'Please enter at least a first and last name', (value) => {
        if (!value) return true;
        return value.trim().indexOf(' ') !== -1;
      }),
    address: addressSchema,
    email: options.requireEmail
      ? yup.string().email('Please enter a valid email').required('Email is required')
      : yup.string().email('Please enter a valid email').optional(),
    companyName: options.isProfessional
      ? yup.string().required('Company name is required')
      : yup.string().optional(),
    isOver18: options.showAgeSelection
      ? yup.boolean().required('Please select an age')
      : yup.boolean().optional(),
  });

  return schema;
};

interface PersonFormProps {
  initialData?: any;
  onSave: (data: any) => void;
  onDelete?: () => void; // Optional delete handler
  // Configuration
  showAgeSelection?: boolean; // Shows Over 18 / Under 18 radio buttons
  hideUnder18?: boolean; // Hide Under 18 option (for executors who must be over 18)
  showEmail?: boolean;
  showProfessionalOption?: boolean;
  professionalLabel?: string;
  submitLabel?: string;
}

export const PersonForm = ({
  initialData = {},
  onSave,
  onDelete,
  showAgeSelection = false,
  hideUnder18 = false,
  showEmail = false,
  showProfessionalOption = false,
  professionalLabel = "Professional",
  submitLabel = "Save"
}: PersonFormProps) => {
  const [isProfessional, setIsProfessional] = React.useState(
    initialData.relationship === 'Professional' || !!initialData.companyName
  );

  const schema = createPersonSchema({
    showAgeSelection: showAgeSelection && !isProfessional,
    requireEmail: showEmail && isProfessional,
    isProfessional
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      fullName: initialData.fullName || '',
      companyName: initialData.companyName || '',
      email: initialData.email || '',
      address: initialData.address || { line1: '', line2: '', city: '', postcode: '' },
      isOver18: initialData.isOver18 ?? (showAgeSelection && hideUnder18 ? true : initialData.isOver18),
      ...initialData
    }
  });

  const onSubmit = (data: any, e?: React.BaseSyntheticEvent) => {
    e?.stopPropagation();
    onSave({
      ...data,
      relationship: isProfessional ? 'Professional' : (initialData.relationship || 'Individual'),
    });
  };

  return (
    <form onSubmit={(e) => { e.stopPropagation(); handleSubmit(onSubmit)(e); }} className="space-y-6">
      {showProfessionalOption && (
        <div className="space-y-6">
          <Label className="text-base font-medium text-gray-700">Type</Label>
          <div className="flex gap-4">
            <ActionCard selected={!isProfessional} onClick={() => setIsProfessional(false)}>Individual</ActionCard>
            <ActionCard selected={isProfessional} onClick={() => setIsProfessional(true)}>{professionalLabel}</ActionCard>
          </div>
        </div>
      )}

      {isProfessional && (
        <>
          <div className="space-y-2">
            <Label className="text-base font-medium text-gray-700">Company Name</Label>
            <Input {...register('companyName')} placeholder="e.g. Smith & Co Solicitors" />
            {errors.companyName && <p className="text-destructive text-sm">{(errors.companyName as any).message}</p>}
          </div>

          <div className="space-y-2">
            <Label className="text-base font-medium text-gray-700">Contact Person Full Name</Label>
            <Input {...register('fullName')} placeholder="" />
            <p className="text-sm text-muted-foreground">The person at the company who will handle the estate</p>
            {errors.fullName && <p className="text-destructive text-sm">{(errors.fullName as any).message}</p>}
          </div>

          {showEmail && (
            <div className="space-y-2">
              <Label className="text-base font-medium text-gray-700">Email</Label>
              <Input type="email" {...register('email')} placeholder="" />
              {errors.email && <p className="text-destructive text-sm">{(errors.email as any).message}</p>}
            </div>
          )}
        </>
      )}

      {!isProfessional && (
        <div className="space-y-3">
          <Label className="text-base font-medium text-gray-700">Full Name</Label>
          <Input {...register('fullName')} placeholder="" />
          {errors.fullName && <p className="text-destructive text-sm">{(errors.fullName as any).message}</p>}
        </div>
      )}

      <div className="space-y-2">
        <AddressSection
          prefix="address"
          label={isProfessional ? "Office Address" : "Address"}
          labelClassName="text-base font-medium text-gray-700"
          register={register}
          setValue={setValue}
          errors={errors}
          control={control}
        />
      </div>

      {!isProfessional && showAgeSelection && (
        <>
          <AgeSelection
            value={watch('isOver18')}
            onChange={(val) => setValue('isOver18', val, { shouldValidate: true })}
            hideUnder18={hideUnder18}
          />
          {errors.isOver18 && <p className="text-destructive text-sm">{(errors.isOver18 as any).message}</p>}
        </>
      )}

      <div className="pt-4">
        {onDelete ? (
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onDelete}
              className="flex-1 text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900"
            >
              Delete Person
            </Button>
            <Button type="submit" disabled={!isValid} variant="primary" className="flex-1">{submitLabel}</Button>
          </div>
        ) : (
          <Button type="submit" disabled={!isValid} variant="primary" className="w-full">{submitLabel}</Button>
        )}
      </div>
    </form>
  );
};
